import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  MessageSquare, Phone, Mail, Check, ArrowRight, Sparkles,
  CheckCircle2, Loader2, ChevronLeft, ShieldCheck, Bell, Bot, CalendarCheck
} from "lucide-react";

const FEATURES = [
  { key: "reminders", label: "Reminder automatici", icon: Bell, defaultOn: true },
  { key: "confirmations", label: "Conferme appuntamenti", icon: CalendarCheck, defaultOn: true },
  { key: "ai_support", label: "Assistenza clienti AI", icon: Bot, defaultOn: true },
];

const STATUS_LABELS = {
  new_request: "Nuova richiesta",
  awaiting_data: "In attesa dati",
  configuring: "In configurazione",
  meta_review: "In verifica Meta",
  test_send: "Test invio",
  active: "Attivo",
  blocked: "Bloccato",
};

export const WhatsAppActivationWizard = ({ open, onOpenChange, defaults = {} }) => {
  const [step, setStep] = useState(0);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [choice, setChoice] = useState("current");
  const [form, setForm] = useState({
    business_name: defaults.business_name || "",
    phone: defaults.phone || "",
    email: defaults.email || "",
  });
  const [features, setFeatures] = useState(
    Object.fromEntries(FEATURES.map(f => [f.key, f.defaultOn]))
  );

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/whatsapp-requests/my-status`, { withCredentials: true })
      .then(r => {
        if (r.data?.has_request) {
          setExisting(r.data.request);
          setStep(99); // status view
        } else {
          setExisting(null);
          setStep(0);
        }
      })
      .catch(() => setExisting(null))
      .finally(() => setLoading(false));
  }, [open]);

  const submit = async () => {
    if (!form.business_name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Compila tutti i campi");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/whatsapp-requests/submit`,
        {
          business_name: form.business_name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim().toLowerCase(),
          current_or_new_number: choice,
          features_requested: FEATURES.filter(f => features[f.key]).map(f => f.key),
        },
        { withCredentials: true }
      );
      setExisting(res.data.request);
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden gap-0" data-testid="wa-wizard">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : step === 99 && existing ? (
          <ExistingStatus existing={existing} onOpenChange={onOpenChange} />
        ) : step === 0 ? (
          <StepIntro onNext={() => setStep(1)} />
        ) : step === 1 ? (
          <StepNumberChoice value={choice} onChange={setChoice} onBack={() => setStep(0)} onNext={() => setStep(2)} />
        ) : step === 2 ? (
          <StepForm
            form={form} setForm={setForm}
            features={features} setFeatures={setFeatures}
            submitting={submitting}
            onBack={() => setStep(1)}
            onSubmit={submit}
          />
        ) : (
          <StepDone onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
};

const Header = ({ step }) => (
  <div className="bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 text-white p-5 relative overflow-hidden">
    <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/10 blur-2xl pointer-events-none" />
    <div className="relative flex items-center gap-3">
      <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shadow-lg">
        <MessageSquare className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Attivazione WhatsApp · Step {step}/3</p>
        <h2 className="font-bold text-lg leading-tight">WhatsApp Business per la tua attività</h2>
      </div>
    </div>
    <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
      <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${(step / 3) * 100}%` }} />
    </div>
  </div>
);

const StepIntro = ({ onNext }) => (
  <div data-testid="wa-step-intro">
    <Header step={1} />
    <div className="p-6 space-y-4">
      <DialogHeader>
        <DialogTitle className="text-xl">Attiva WhatsApp Business in pochi minuti</DialogTitle>
      </DialogHeader>
      <p className="text-sm text-muted-foreground">
        Invia <strong>reminder automatici</strong>, <strong>conferme appuntamenti</strong> e
        offri <strong>assistenza clienti AI</strong>. Il team SmartBooker configura tutto per te.
      </p>
      <div className="grid grid-cols-3 gap-2">
        {FEATURES.map(f => (
          <div key={f.key} className="p-3 rounded-xl bg-muted/40 text-center">
            <f.icon className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
            <p className="text-[10px] font-medium">{f.label}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
        Solo 90 secondi · Nessun token tecnico richiesto
      </p>
    </div>
    <div className="border-t bg-muted/20 p-4 flex justify-end">
      <Button onClick={onNext} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="wa-step-intro-next">
        Continua <ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
    </div>
  </div>
);

const StepNumberChoice = ({ value, onChange, onBack, onNext }) => (
  <div data-testid="wa-step-number">
    <Header step={2} />
    <div className="p-6 space-y-3">
      <DialogHeader>
        <DialogTitle className="text-lg">Vuoi usare il numero attuale o uno nuovo?</DialogTitle>
      </DialogHeader>
      {[
        { key: "current", title: "Uso il mio numero WhatsApp Business attuale", sub: "Manteniamo il numero che già usi con i tuoi clienti" },
        { key: "new", title: "Voglio usare un nuovo numero dedicato", sub: "Ti aiutiamo a procurarti un numero verificato" },
      ].map(o => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${value === o.key ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 shadow-sm" : "border-muted hover:border-muted-foreground/30"}`}
          data-testid={`wa-choice-${o.key}`}
        >
          <div className="flex items-start gap-3">
            <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${value === o.key ? "border-emerald-500 bg-emerald-500" : "border-muted-foreground/30"}`}>
              {value === o.key && <Check className="w-3 h-3 text-white" />}
            </div>
            <div>
              <p className="font-semibold text-sm">{o.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{o.sub}</p>
            </div>
          </div>
        </button>
      ))}
    </div>
    <div className="border-t bg-muted/20 p-4 flex justify-between">
      <Button variant="ghost" onClick={onBack} className="rounded-full"><ChevronLeft className="w-4 h-4 mr-1" />Indietro</Button>
      <Button onClick={onNext} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="wa-step-number-next">Avanti <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
    </div>
  </div>
);

const StepForm = ({ form, setForm, features, setFeatures, submitting, onBack, onSubmit }) => (
  <div data-testid="wa-step-form">
    <Header step={3} />
    <div className="p-6 space-y-4">
      <DialogHeader>
        <DialogTitle className="text-lg">Pochi dati e abbiamo finito</DialogTitle>
      </DialogHeader>
      <div className="space-y-2.5">
        <Input
          placeholder="Nome attività"
          value={form.business_name}
          onChange={(e) => setForm({ ...form, business_name: e.target.value })}
          className="h-11"
          data-testid="wa-form-business"
        />
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Numero WhatsApp (+39 ...)"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="h-11 pl-9"
            data-testid="wa-form-phone"
          />
        </div>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="email"
            placeholder="Email referente"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="h-11 pl-9"
            data-testid="wa-form-email"
          />
        </div>
      </div>
      <div className="space-y-2 pt-2">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Cosa vuoi attivare</p>
        {FEATURES.map(f => (
          <label key={f.key} className="flex items-center gap-3 p-3 rounded-xl border-2 border-muted hover:border-muted-foreground/30 cursor-pointer transition-all" data-testid={`wa-feature-${f.key}`}>
            <input
              type="checkbox"
              checked={!!features[f.key]}
              onChange={(e) => setFeatures({ ...features, [f.key]: e.target.checked })}
              className="w-4 h-4 accent-emerald-500"
            />
            <f.icon className="w-4 h-4 text-emerald-500" />
            <span className="text-sm font-medium">{f.label}</span>
          </label>
        ))}
      </div>
    </div>
    <div className="border-t bg-muted/20 p-4 flex justify-between">
      <Button variant="ghost" onClick={onBack} className="rounded-full" disabled={submitting}><ChevronLeft className="w-4 h-4 mr-1" />Indietro</Button>
      <Button onClick={onSubmit} disabled={submitting} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="wa-form-submit">
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
        Richiedi attivazione gratuita
      </Button>
    </div>
  </div>
);

const StepDone = ({ onClose }) => (
  <div data-testid="wa-step-done">
    <Header step={3} />
    <div className="p-8 text-center space-y-4">
      <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950/40 flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-9 h-9 text-emerald-500" />
      </div>
      <h3 className="text-xl font-bold">Perfetto 🎉</h3>
      <p className="text-sm text-muted-foreground">
        Abbiamo ricevuto la tua richiesta.<br />
        Il team SmartBooker configurerà WhatsApp per te.
      </p>
      <p className="text-xs text-muted-foreground">Ti scriviamo via email entro 24h.</p>
    </div>
    <div className="border-t bg-muted/20 p-4 flex justify-end">
      <Button onClick={onClose} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="wa-step-done-close">Ho capito</Button>
    </div>
  </div>
);

const ExistingStatus = ({ existing, onOpenChange }) => {
  const isActive = existing.status === "active";
  return (
    <div data-testid="wa-existing-status">
      <Header step={3} />
      <div className="p-6 text-center space-y-4">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${isActive ? "bg-emerald-100 dark:bg-emerald-950/40" : "bg-amber-100 dark:bg-amber-950/40"}`}>
          {isActive ? <CheckCircle2 className="w-9 h-9 text-emerald-500" /> : <Loader2 className="w-9 h-9 text-amber-500 animate-spin" />}
        </div>
        <div>
          <h3 className="text-lg font-bold">{isActive ? "WhatsApp attivo 🎉" : "Richiesta in lavorazione"}</h3>
          <p className="text-xs text-muted-foreground mt-1">Status: <span className="font-semibold">{STATUS_LABELS[existing.status] || existing.status}</span></p>
        </div>
        {!isActive && (
          <p className="text-sm text-muted-foreground">Il team SmartBooker sta configurando il tuo WhatsApp.<br />Riceverai un'email quando sarà attivo.</p>
        )}
        {isActive && (
          <p className="text-sm text-muted-foreground">Reminder, conferme e assistenza ora partono automaticamente.</p>
        )}
      </div>
      <div className="border-t bg-muted/20 p-4 flex justify-end">
        <Button onClick={() => onOpenChange(false)} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="wa-existing-close">Chiudi</Button>
      </div>
    </div>
  );
};

export default WhatsAppActivationWizard;
